import React, {
  useCallback, useMemo, useState,
} from 'react';
import * as Styles from './AiBuilderCreateSiteAgent.styles';
import { CREATE_SITE_AGENT_TABS } from '../AiBuilderCreateSite/AiBuilderCreateSite.enum';
import starImage from '@/media/onboarding/aiBuilder/star-to-background.png';
import { NameSegmentStep } from './components/NameSegmentStep';
import { InfosStep } from './components/InfosStep';
import imageStepName from '@/media/onboarding/aiBuilder/create-site-and-agent-step-1-br.png';
import imageStepNameEs from '@/media/onboarding/aiBuilder/create-site-and-agent-step-1-es.png';
import imageStepInfo from '@/media/onboarding/aiBuilder/create-site-and-agent-step-2-br.png';
import imageStepInfoEs from '@/media/onboarding/aiBuilder/create-site-and-agent-step-2-es.png';
import iconMaia from '@/media/onboarding/aiBuilder/AiAnimation.webp';
import { useResize } from '@/hooks/useResize';
import { isBRServer } from '@/utils/Validators/validation';

const AiBuilderCreateSiteAgent = ({
  locales,
  onContinue,
  userCountry,
  aiBuilderAgentData,
  setAiBuilderAgentData,
}) => {
  const [currentTab, setCurrentTab] = useState(CREATE_SITE_AGENT_TABS.NAME_SEGMENT);

  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 600;

  const segmentFormatted = useMemo(() => {
    const options = locales.steps[CREATE_SITE_AGENT_TABS.NAME_SEGMENT].optionsList || [];

    const optionFound = options.find(option => option.label && option.label.toLowerCase() === aiBuilderAgentData.segment.toLowerCase());

    if (optionFound && optionFound.value) {
      return optionFound.value;
    }

    return aiBuilderAgentData.segment;
  }, [aiBuilderAgentData.segment, locales]);

  const projectData = useMemo(() => ({
    company: aiBuilderAgentData.name,
    segment: segmentFormatted,
    description: aiBuilderAgentData.description,
    phone: aiBuilderAgentData.phone,
    email: aiBuilderAgentData.email,
    toneOfVoice: aiBuilderAgentData.toneOfVoice,
    language: isBRServer ? 'pt-br' : 'es',
  }), [
    aiBuilderAgentData.name,
    segmentFormatted,
    aiBuilderAgentData.description,
    aiBuilderAgentData.phone,
    aiBuilderAgentData.email,
    aiBuilderAgentData.toneOfVoice,
  ]);

  const handleNameSegmentStepContinue = useCallback(() => {
    setCurrentTab(CREATE_SITE_AGENT_TABS.INFOS);
  }, []);

  const handleInfosStepContinue = useCallback(() => {
    onContinue && onContinue({ projectData });
  }, [onContinue, projectData]);

  const nameSegmentStepProps = useCallback(() => ({
    continueButtonClick: handleNameSegmentStepContinue,
    nameStepLocales: locales.steps[CREATE_SITE_AGENT_TABS.NAME_SEGMENT],
    fieldNameValue: aiBuilderAgentData.name,
    setFieldNameValue: value => setAiBuilderAgentData(prev => ({ ...prev, name: value })),
    fieldSegmentValue: aiBuilderAgentData.segment,
    setFieldSegmentValue: value => setAiBuilderAgentData(prev => ({ ...prev, segment: value })),
    mobile,
    iconMaia,
  }), [
    aiBuilderAgentData.name,
    handleNameSegmentStepContinue,
    locales,
    aiBuilderAgentData.segment,
    mobile,
    setAiBuilderAgentData,
    iconMaia,
  ]);

  const infosStepProps = useCallback(() => ({
    continueButtonClick: handleInfosStepContinue,
    infosStepLocales: locales.steps[CREATE_SITE_AGENT_TABS.INFOS],
    userCountry,
    fieldDescriptionValue: aiBuilderAgentData.description,
    setFieldDescriptionValue: value => setAiBuilderAgentData(prev => ({ ...prev, description: value })),
    fieldTelephoneValue: aiBuilderAgentData.phone,
    setFieldTelephoneValue: value => setAiBuilderAgentData(prev => ({ ...prev, phone: value })),
    fieldEmailValue: aiBuilderAgentData.email,
    setFieldEmailValue: value => setAiBuilderAgentData(prev => ({ ...prev, email: value })),
    mobile,
    iconMaia,
    setToneOfVoiceValue: value => setAiBuilderAgentData(prev => ({ ...prev, toneOfVoice: value })),
    toneOfVoiceValue: aiBuilderAgentData.toneOfVoice,
  }), [
    locales,
    aiBuilderAgentData.description,
    aiBuilderAgentData.phone,
    aiBuilderAgentData.email,
    handleInfosStepContinue,
    aiBuilderAgentData.toneOfVoice,
    setAiBuilderAgentData,
    mobile,
    userCountry,
    iconMaia,
  ]);

  const tabContent = {
    [CREATE_SITE_AGENT_TABS.NAME_SEGMENT]: <NameSegmentStep {...nameSegmentStepProps()} />,
    [CREATE_SITE_AGENT_TABS.INFOS]: <InfosStep {...infosStepProps()} />,
  };

  const getImagesByStep = {
    [CREATE_SITE_AGENT_TABS.NAME_SEGMENT]: isBRServer ? imageStepName : imageStepNameEs,
    [CREATE_SITE_AGENT_TABS.INFOS]: isBRServer ? imageStepInfo : imageStepInfoEs,
  };

  return (
    <Styles.Wrapper data-testid="ai-builder-create-site-agent">
      <Styles.PromoPanel>
        <Styles.PromoPanelInner>
          <Styles.PromoTitleContainer>
            <Styles.PromoTitle>
              {locales.steps[currentTab].promoBannerTitle}
            </Styles.PromoTitle>
          </Styles.PromoTitleContainer>

          <Styles.PromoImage src={getImagesByStep[currentTab]} alt="promo banner" />
        </Styles.PromoPanelInner>
        <Styles.StarImage src={starImage} alt="loading-star" />
      </Styles.PromoPanel>

      <Styles.FormContainer>
        {tabContent[currentTab]}
      </Styles.FormContainer>
    </Styles.Wrapper>
  );
};

export default AiBuilderCreateSiteAgent;
